import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { clsx as classNames } from 'clsx';
import Spinner from '../spinner';

const BaseComponent = styled.button`
	display: inline-block;
	transition: color 0.15s ease-in-out, background-color 0.15s ease-in-out,
		box-shadow 0.15s ease-in-out;
	border: 0.125rem solid transparent;
	border-radius: 0.25rem;
	background-color: transparent;
	cursor: pointer;
	padding: 0.375rem 1rem;
	vertical-align: middle;
	text-align: center;
	color: hsl(var(--foreground));
	line-height: 1.5rem;
	font-family: ${({ theme }) => theme['base-font-family']};
	font-size: 1rem;
	font-weight: 500;
	white-space: nowrap;

	&:focus {
		outline: 0;
		box-shadow: 0 0 0 0.2rem hsl(var(--ring) / 0.5);
	}

	&.primary {
		background-color: ${({ darken }) => darken ? 'hsl(var(--blue-900))' : 'hsl(var(--primary))'};
		color: hsl(var(--primary-foreground));

		&:hover {
			background-color: ${({ darken }) => darken ? 'hsl(var(--blue-900))' : 'hsl(var(--blue-900))'};
		}
	}

	&.secondary {
		background-color: hsl(var(--secondary));
		color: hsl(var(--secondary-foreground));

		&:hover {
			background-color: ${({ lighten }) => lighten ? 'hsl(var(--midnight-100))' : 'hsl(var(--midnight-200))'};
		}
	}

	&.warning {
		background-color: hsl(var(--yellow-base));
		color: hsl(var(--foreground));

		&:hover {
			filter: brightness(0.95);
		}
	}

	&.danger {
		background-color: hsl(var(--destructive));
		color: hsl(var(--destructive-foreground));

		&:hover {
			filter: brightness(0.95);
		}
	}

	&.ghost-primary {
		color: hsl(var(--primary));
		background-color: transparent;
		border-color: hsl(var(--primary));

		&:hover {
			color: hsl(var(--primary-foreground));
			background-color: hsl(var(--primary));
			border-color: hsl(var(--primary));
		}
	}

	&.ghost-secondary {
		color: hsl(var(--secondary));
		background-color: transparent;
		border-color: hsl(var(--secondary));

		&:hover {
			color: hsl(var(--secondary-foreground));
			background-color: hsl(var(--secondary));
			border-color: hsl(var(--secondary));
		}
	}

	&.ghost-warning {
		color: hsl(var(--yellow-base));
		background-color: transparent;
		border-color: hsl(var(--yellow-base));

		&:hover {
			color: hsl(var(--foreground));
			background-color: hsl(var(--yellow-base));
			border-color: hsl(var(--yellow-base));
		}
	}

	&.ghost-danger {
		color: hsl(var(--destructive));
		background-color: transparent;
		border-color: hsl(var(--destructive));

		&:hover {
			color: hsl(var(--destructive-foreground));
			background-color: hsl(var(--destructive));
			border-color: hsl(var(--destructive));
		}
	}

	&.blank {
		background-color: transparent;

		&:hover {
			background-color: transparent;
		}
	}

	&.lg {
		padding: 0.5rem 1.25rem;
		font-size: 1.25rem;
		line-height: 1.5;
		border-radius: 0.3rem;
	}

	&.sm {
		padding: 0.25rem 0.75rem;
		font-size: 0.875rem;
		line-height: 1.5;
		border-radius: 0.2rem;
	}

	&.xs {
		padding: 0.25rem 0.625rem;
		font-size: 0.75rem;
		line-height: 1;
		border-radius: 0.2rem;
	}

	&.block {
		display: block;
		width: 100%;
	}

	&.disabled,
	&[disabled] {
		opacity: 0.25;
		pointer-events: none;
	}
`;

const Content = styled.div`
	display: flex;
	flex-direction: row;
	align-items: center;
	justify-content: center;
`;

const PaddedSpinner = styled(Spinner)`
	position: relative;
	top: 1px;
	margin-left: -0.625rem;
	margin-right: 0.25rem;
`;

class Button extends PureComponent {
	static propTypes = {
		variant: PropTypes.oneOf([
			'primary', 'secondary', 'warning', 'danger', 'ghost-primary'
		]),
		size: PropTypes.string,
		block: PropTypes.bool,
		disabled: PropTypes.bool,
		type: PropTypes.string,
		loading: PropTypes.bool,
		lighten: PropTypes.bool,
		darken: PropTypes.bool,
		blank: PropTypes.bool
	};

	renderSpinner = () => {
		const { children, loading, block } = this.props;

		return (
			<Content block={block}>
				{ loading && (
					<PaddedSpinner small />
				)}

				{ children }
			</Content>
		);
	};

	render() {
		const {
			className,
			variant = 'primary',
			size,
			block,
			disabled = false,
			blank = false,
			type = 'button',
			loading = false,
			children,
			...props
		} = this.props;

		const classes = classNames(
			className,
			variant,
			block && 'block',
			size && `${size}`,
			disabled && 'disabled',
			blank && 'blank'
		);

		return (
			<BaseComponent {...props} className={classes} type={type} disabled={disabled}>
				{ loading ? this.renderSpinner() : children }
			</BaseComponent>
		);
	}
}

export default Button;
